import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { popularRetroFormats, TCG } from '../../data/formats'
import FormatName from '../ygo/FormatName'
import PlayerName from '../card/name/PlayerName'
import ProTag from '../general/ProTag'
import { month } from '../../helper/date'
import { VIEWPORT_MOBILE } from '../../styles/media'
import EventName from '../card/name/EventName'
import RelativeTime from './RelativeTime'
import DeckLinkCommunity from './DeckLinkCommunity'
import TagOld from '../general/TagOld'
import Name from '../card/name/Name'
import translate from '../../helper/translate'
import { getSeasonName } from '../../helper/card'
import Avatar from '../user/Avatar'
import deckTags from '../../data/deckTags'
import Icon from '../general/Icon'
import { translationsMapping } from '../../../config'
import { withRouter } from 'react-router-dom'
import { DeckFolder } from '../../pages/deck/Toolbar'

const DeckMeta = ({ browsePublicDecks, canEdit, deck, deckPage, folders, hideCommunity, isResult, loadedFolder, location, match, mydecks, nowrap, profile, showAddedToFavoritesAt, showArchivedAt, showCreatedAt, showEventDate, t, userLanguage, viewport }) => {
	const facet = match.params.facet || null
	const tags = deck.tags ? deck.tags.map(tag => deckTags.find(item => item.id === tag)).filter(tag => tag && tag.slug !== facet) : []
	const deckFolder = folders?.find(folder => folder.id === deck.folder)

	let date = deck.updatedAt

	if (showCreatedAt) {
		date = deck.createdAt
	} else if (showArchivedAt) {
		date = deck.archived
	} else if (showAddedToFavoritesAt) {
		date = deck.userStar
	}

	const isRetroFormat = deck.format === TCG && popularRetroFormats.includes(deck.season?.id)

	return (
		<StyledDeckMeta>
			<Author>
				{!deck.hasResult && ((canEdit && !browsePublicDecks) || profile || deckPage || deck.format !== TCG) && !isRetroFormat && (
					<Format>
						{deck.format && <FormatName abbr format={deck.format} />}
						{(!browsePublicDecks || profile) && deck.season && <>&nbsp;{getSeasonName(deck.season)}</>}
					</Format>
				)}

				{isRetroFormat && (
					<>
						<RetroFormatName>{deck.season.name}</RetroFormatName>
					</>
				)}

				{!showEventDate && deck.hasResult && (
					<PlayerName
						country={deck.eventPlayers[0].playerCountry}
						name={deck.eventPlayers[0].playerName}
						username={deck.eventPlayers[0].playerUsername}
					/>
				)}

				{!deck.hasResult && (!canEdit || browsePublicDecks) && !profile && (
					<>
						{(deck.format !== TCG || isRetroFormat) && (
							<>
								&nbsp;&middot;&nbsp;
							</>
						)}
						{deck.user.subscribed && (
							<Avatar width={16} avatar={deck.user.avatar} />
						)}
						{deck.user.username}
						{deck.user.subscribed === 'pro' && <ProTag title={deck.user.title} />}
					</>
				)}

				{deck.hasResult && !isResult && (
					<>
						<Time>
							{!showEventDate && <>&nbsp;&middot;&nbsp;</>}

							{month(deck.eventPlayers[0].event.date)}

							{showEventDate && viewport === VIEWPORT_MOBILE && (
								<>
									&nbsp;&middot;&nbsp;
									<EventName
										name={deck.eventPlayers[0].event.name}
									/>
								</>
							)}
						</Time>
					</>
				)}

				{!deck.hasResult && (!mydecks || loadedFolder) && (!mydecks || !deck.folder || deckFolder?.slug === loadedFolder) && (
					<>
						&nbsp;
						<Time>
							&middot;&nbsp;
							{showAddedToFavoritesAt && `${t('general.starredAt')} `}
							{showArchivedAt && `${t('general.archived')} `}
							<RelativeTime date={date} />
						</Time>
					</>
				)}

				{!nowrap && !mydecks && tags.length > 0 && (
					<>
						&nbsp;&middot;&nbsp;
						<Tags>
							{tags.slice(0, 3).map(tag => (
								<Tag key={tag.id}>
									<Icon name="tag" />
									{tag[`name_${translationsMapping[userLanguage]}`]}
								</Tag>
							))}
						</Tags>
					</>
				)}

				{mydecks && deck.folder && loadedFolder !== deckFolder?.slug && (
					<>
						<DeckFolder>&nbsp;&middot;&nbsp;<Icon name="folder" />{deckFolder?.name}</DeckFolder>
					</>
				)}
			</Author>
			{deck.visibility === 'public' && !hideCommunity && !deckPage && (
				<DeckLinkCommunity deck={deck} />
			)}
		</StyledDeckMeta>
	)
}

DeckMeta.propTypes = {
	browsePublicDecks: PropTypes.bool,
	canEdit: PropTypes.bool,
	deck: PropTypes.object.isRequired,
	deckPage: PropTypes.bool,
	folders: PropTypes.array,
	hideCommunity: PropTypes.bool,
	isResult: PropTypes.bool,
	loadedFolder: PropTypes.string,
	location: PropTypes.object.isRequired,
	match: PropTypes.object.isRequired,
	mydecks: PropTypes.bool,
	nowrap: PropTypes.bool,
	profile: PropTypes.bool,
	showAddedToFavoritesAt: PropTypes.bool,
	showArchivedAt: PropTypes.bool,
	showCreatedAt: PropTypes.bool,
	showEventDate: PropTypes.bool,
	t: PropTypes.func.isRequired,
	userLanguage: PropTypes.string.isRequired,
	viewport: PropTypes.string.isRequired
}

DeckMeta.defaultProps = {
	browsePublicDecks: false,
	canEdit: false,
	deckPage: false,
	folders: null,
	hideCommunity: false,
	isResult: false,
	loadedFolder: null,
	mydecks: false,
	nowrap: false,
	profile: false,
	showAddedToFavoritesAt: false,
	showArchivedAt: false,
	showCreatedAt: false,
	showEventDate: false
}

const StyledDeckMeta = styled.div`
	display: flex;
	margin-top: 0.15rem;

	${DeckFolder} {
		color: ${props => props.theme.textVeryLight};
	}
`

const Format = styled.span`
	font-size: 0.8rem;
`

const RetroFormatName = styled.span`
	color: ${props => props.theme.gold2};
	font-size: 0.8rem;
	font-weight: 600;
	text-transform: uppercase;
`

export const Author = styled.div`
	color: ${props => props.theme.textLight};
	font-size: 0.9rem;
	font-weight: 400;

    align-items: flex-end;
	display: flex;
    width: fit-content;

    white-space: nowrap;

	img, svg {
		border-radius: 50%;
		height: 16px;
		width: 16px;
		margin-right: 0.25rem;
	}

	${TagOld} {
		font-size: 0.8em;
		margin-left: 0.25rem;
	}

	${Name} {
		display: inline-flex;

		img {
			border-radius: 1px;
			height: 12px;
			width: 12px;
		}
	}
`

export const Time = styled.span`
	color: ${props => props.theme.textVeryLight};
	font-size: 0.8rem;
`

const Tags = styled.span`
	color: ${props => props.theme.textVeryLight};
	font-size: 0.7rem;
	font-weight: 500;

	display: flex;
	gap: 0.35rem;
`

const Tag = styled.span`
	display: flex;
	align-items: center;

	svg {
		height: 12px;
		width: 12px;
		margin-right: 0.15rem;
	}
`

const enhance = compose(
	connect(state => ({
		folders: state.deckbuilder.folders,
		userLanguage: state.persist.language,
		viewport: state.app.viewport
	})),
	translate('general'),
	withRouter
)

export default enhance(DeckMeta)
